import { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import Loader from '@/components/Loader';
import { useGetAffidavitsForWorkOrder } from '@/services/affidavitService';
import { useGenerateServiceFilePackage } from '@/services/filePackageService';
import { useGetServiceById } from '@/services/pe';
import { useFilePackageStore } from '@/stores/filePackageStore';
import type { WorkOrderResponse } from '@/types/workOrder.types';
import { displayError } from '@/lib/error';
import { AFFIDAVIT_MODE_OPTIONS, ExportFilePackageForm, exportFilePackageSchema } from './exportFilePackage.schema';

export const FILE_PACKAGE_PREPARING_MESSAGE =
  'The package is being prepared and will be available on the downloads page for export.';

interface FilePackageWorkOrderRowProps {
  index: number;
  workOrder: WorkOrderResponse;
}

const FilePackageWorkOrderRow: FC<FilePackageWorkOrderRowProps> = ({ index, workOrder }) => {
  const { data: affidavits, isLoading, isError } = useGetAffidavitsForWorkOrder(workOrder.workOrderId);
  const hasAttempts = (workOrder._count?.workOrderAttempts ?? 0) > 0;

  const modeOptions = AFFIDAVIT_MODE_OPTIONS.map((option) => ({
    label: option.label,
    value: option.value,
    disabled: option.value === 'complete' && !hasAttempts,
  }));
  const affidavitOptions = (affidavits ?? []).map((affidavit) => ({
    label: affidavit.title || affidavit.name,
    value: affidavit.code,
  }));

  return (
    <div className="grid grid-cols-1 md:grid-cols-[120px_1fr_1fr] gap-3 md:items-start py-3 border-b border-bordercolor last:border-b-0">
      <span className="font-medium md:pt-2" data-testid="file-package-work-order-number">
        {workOrder.number}
      </span>
      <FormFieldSelect
        name={`workOrders.${index}.affidavitMode`}
        options={modeOptions}
        placeholder="Affidavit Mode"
        tooltip={hasAttempts ? undefined : 'Complete affidavit needs at least one attempt'}
      />
      <div className="flex flex-col gap-1">
        <FormFieldSelect
          name={`workOrders.${index}.affidavitCode`}
          sortOptions
          options={affidavitOptions}
          placeholder="Affidavit"
          isLoading={isLoading}
          disabled={isError}
        />
        {isError && <span className="text-error text-sm">Failed to load affidavits</span>}
      </div>
    </div>
  );
};

interface ExportFilePackageDialogProps {
  serviceId: string;
  caseNumber?: string;
  onClose: () => void;
}

const ExportFilePackageDialog: FC<ExportFilePackageDialogProps> = ({ serviceId, caseNumber, onClose }) => {
  // The maintenance list can be filtered, so load the whole file to list every work order.
  const { data: serviceResponse, isLoading, isError } = useGetServiceById(serviceId);
  const generatePackage = useGenerateServiceFilePackage();
  const trackFilePackage = useFilePackageStore((state) => state.track);

  const workOrders = useMemo(
    () => (serviceResponse?.data.workOrders ?? []).filter((workOrder) => !workOrder.deletedAt),
    [serviceResponse]
  );

  const form = useForm<ExportFilePackageForm>({
    resolver: zodResolver(exportFilePackageSchema),
    mode: 'onChange',
    defaultValues: { workOrders: [] },
  });

  // Reset only when the set of work orders changes, so a refetch of the same
  // file (e.g. stale cache refreshed on mount) keeps the user's selections.
  const workOrderIdsKey = workOrders.map((workOrder) => workOrder.workOrderId).join(',');
  useEffect(() => {
    form.reset({
      workOrders: (workOrderIdsKey ? workOrderIdsKey.split(',') : []).map((workOrderId) => ({
        workOrderId,
        affidavitMode: undefined,
        affidavitCode: '',
      })),
    });
  }, [workOrderIdsKey, form]);

  const selections = useWatch({ control: form.control, name: 'workOrders' });
  const isComplete =
    workOrders.length > 0 &&
    selections?.length === workOrders.length &&
    selections.every((selection) => !!selection.affidavitMode && !!selection.affidavitCode);

  const onSubmit = (values: ExportFilePackageForm) => {
    generatePackage.mutate(
      {
        serviceId,
        workOrders: values.workOrders.map((selection) => ({
          workOrderId: selection.workOrderId,
          affidavitCode: selection.affidavitCode,
          blankTemplate: selection.affidavitMode === 'blank',
        })),
      },
      {
        onSuccess: (filePackage) => {
          trackFilePackage(filePackage.filePackageId, serviceId);
          toast({ description: FILE_PACKAGE_PREPARING_MESSAGE, duration: 5000 });
          onClose();
        },
        onError: (error) => {
          displayError(error);
        },
      }
    );
  };

  const renderBody = () => {
    if (isError) {
      return <p className="text-error py-4">Failed to load the work orders for this file.</p>;
    }
    if (workOrders.length === 0) {
      return <p className="text-placeholder py-4">This file has no work orders to export.</p>;
    }
    return (
      <div className="flex flex-col">
        <div className="hidden md:grid grid-cols-[120px_1fr_1fr] gap-3 pb-2 text-sm font-semibold border-b border-bordercolor">
          <span>W/O #</span>
          <span>Affidavit Mode</span>
          <span>Affidavit</span>
        </div>
        <div className="max-h-[50vh] overflow-auto custom-scrollbar">
          {workOrders.map((workOrder, index) => (
            <FilePackageWorkOrderRow key={workOrder.workOrderId} index={index} workOrder={workOrder} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !generatePackage.isPending && onClose()}>
      <DialogContent className="md:max-w-[60vw] max-w-[95vw]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Export File Package{caseNumber ? ` - ${caseNumber}` : ''}
          </DialogTitle>
          <DialogDescription>Select an Affidavit Mode and an Affidavit for each work order.</DialogDescription>
        </DialogHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <Loader isLoading={isLoading} className="py-8">
              {renderBody()}
            </Loader>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-[32px]"
                onClick={onClose}
                disabled={generatePackage.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="h-[32px] min-w-[150px]"
                disabled={!isComplete || generatePackage.isPending}
                isLoading={generatePackage.isPending}
              >
                Generate Package
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default ExportFilePackageDialog;
